import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import _ from 'lodash';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { countries } from 'country-data';
import {
  Form,
  Dropdown,
  Icon,
  Grid,
  Button,
  Segment,
  Message,
  MessageHeader,
  Divider,
  Label,
  Input,
  Checkbox,
  Popup,
  Radio,
} from 'semantic-ui-react';

import { DyzioPrimaryButton } from 'dyzio-shared-components/lib/components/dyzio-buttons';
import HashtagsInput from '../../components/hashtags-input';
import TargetLink from '../../components/target-link';
import { DYZIO_CURRENCIES, CURRENCY_GBP, renderCurrencySymbol } from '../../utils/helpers/currencies';
import hasOrganisation from '../../hocs/has-organisation';

import { transformHashtagstIntoArray } from '../../utils/hashtags-utils';

const currencyDropDownStyle = { marginTop: 8, marginBottom: 8 };
const requiredLabelStyle = { color: 'red' };
const RequiredLabel = () => (<span style={requiredLabelStyle}>*</span>);

const EMV_HELP_LINK = 'https://help.dyzio.co/en/articles/4156487-estimated-earned-media-value-emv-report';
const MANAGE_ORG_LINK = '/team/manage';

const FEATURE_TOGGLE_STICKERS = true;

export const FORM_TYPES = {
  BASIC: 'BASIC',
  ADVANCED: 'ADVANCEE',
};

const dividerStyle = {
  width: '100%',
  height: 5,
};

const radioLabelStyle = {
  'vertical-align': 'super',
};

const marginBottom10 = {
  marginBottom: 10,
};

const borderStyle = {
  marginTop: 20,
  padding: 10,
  border: '1px solid',
};

const cmpStyle = {
  marginTop: 5,
};

const initialEmptyState = {
  name: '',
  budget: '',
  liveDate: '',
  endDate: '',
  hashtags: '',
  currency: '',
  impressionsCPM: '',
  engagementsCPM: '',
  ieRatio: '',
  campaignCode: '',
  sendChaseEmails: undefined,
  brand: '',
  country: null,
  useEstimatedReachAndImpressions: false,
};

class NewCampaignForm extends Component {
  constructor(props) {
    super(props);
    const { inEditMode } = props;
    const initialState = inEditMode ? _.pick(props, [
      'name',
      'budget',
      'liveDate',
      'endDate',
      'hashtags',
      'currency',
      'impressionsCPM',
      'engagementsCPM',
      'ieRatio',
      'campaignCode',
      'sendChaseEmails',
      'brand',
      'country',
      'useEstimatedReachAndImpressions',
    ]) : initialEmptyState;
    this.state = initialState;
  }

  onInputChanged = (event, { name, value, checked }) => {
    this.setState({
      [name]: value || checked,
    });
  }

  onCurrencyChanged = (event, { value }) => {
    this.onInputChanged(event, { name: 'currency', value });
  }

  onLiveDateChange = date => {
    this.setState({
      liveDate: date,
    });
  }

  onEndDateChange = date => {
    this.setState({
      endDate: date,
    });
  }

  shouldDisableSubmitButton = () => {
    const { campaignType } = this.props;
    const { name, hashtags, budget, liveDate, endDate } = this.state;
    const isNameEmpty = _.isEmpty(name);
    const noHashtags = _.isEmpty(hashtags);
    const noBudget = _.isEmpty(_.toString(budget));
    const noLiveDate = _.isEmpty(liveDate);
    const noEndDate = _.isEmpty(endDate);

    if (campaignType === 'ACR') {
      return isNameEmpty;
    }
    return isNameEmpty || noHashtags || noBudget || noLiveDate || noEndDate;
  }

  handleFormSubmit = () => {
    const {
      name,
      budget,
      currency,
      liveDate,
      endDate,
      hashtags,
      impressionsCPM,
      engagementsCPM,
      ieRatio,
      campaignCode,
      sendChaseEmails,
      brand,
      country,
      useEstimatedReachAndImpressions,
    } = this.state;
    const { onFormSubmit, campaignType, organisation } = this.props;
    const scanUnauthorisedStories = _.get(organisation, 'isStickerScannerEnabled');
    onFormSubmit({
      name,
      budget,
      currency,
      liveDate,
      endDate,
      campaignType,
      hashtags,
      impressionsCPM,
      engagementsCPM,
      ieRatio,
      campaignCode,
      sendChaseEmails,
      brand,
      country,
      scanUnauthorisedStories,
      useEstimatedReachAndImpressions,
    });
  }

  render() {
    const { campaignType, onBackClick, inEditMode, formType, organisation } = this.props;

    const {
      name,
      budget,
      currency,
      liveDate,
      endDate,
      hashtags,
      impressionsCPM,
      engagementsCPM,
      ieRatio,
      campaignCode,
      sendChaseEmails,
      brand,
      country,
      useEstimatedReachAndImpressions,
    } = this.state;

    const countryOptions = _.map(_.reject(countries.all, item => _.isEmpty(item.emoji)), item => ({
      text: `${item.emoji} ${item.name}`,
      key: item.alpha2,
      value: item.alpha2,
    }));

    countryOptions.unshift({
      text: '<<< No Country >>>',
      style: { color: 'gray' },
      key: 'no-country',
      value: null,
    });

    const isSubmitButtonDisabled = this.shouldDisableSubmitButton();

    const tags = transformHashtagstIntoArray(hashtags);

    const liveDateMoment = liveDate && moment(liveDate).isValid() ? moment(liveDate) : false;
    const endDateMoment = endDate && moment(endDate).isValid() ? moment(endDate) : false;

    const inAdvancedFormMode = formType === FORM_TYPES.ADVANCED;
    const noPadding = { padding: 0 };
    const isStickerScannerEnabled = _.get(organisation, 'isStickerScannerEnabled');
    const isEstimatedReachAndImpressionsEnabled = _.get(organisation, 'isEstimatedReachAndImpressionsEnabled');
    const upgradePlan = !isStickerScannerEnabled || !isEstimatedReachAndImpressionsEnabled;

    const safeEndDate = endDateMoment ? endDateMoment.set({ hour: 15, minute: 15 }) : null;
    const safeLiveDate = liveDateMoment ? liveDateMoment.set({ hour: 15, minute: 15 }) : null;

    const startsInThePast = safeLiveDate ? moment(safeLiveDate).isBefore(moment().startOf('day')) : false;

    const liveDateChanged = liveDate !== this.props.liveDate;

    const shouldWarnPastLiveDate = (startsInThePast && !inEditMode) || (inEditMode && liveDateChanged && startsInThePast);

    return (
      <Form onSubmit={this.handleFormSubmit}>
        <Segment basic={!inAdvancedFormMode} style={inAdvancedFormMode ? null : noPadding}>
          <Form.Field>
            <label htmlFor="campaign-name">Campaign name<RequiredLabel /></label>
            <Form.Input
              onChange={this.onInputChanged}
              placeholder="e.g. Sky Westworld"
              name="name"
              value={name}
            />
          </Form.Field>

          {campaignType !== 'ACR' &&
          <Form.Field>
            <label htmlFor="campaign-brand">Brand</label>
            <Form.Input
              onChange={this.onInputChanged}
              placeholder="e.g. Olay"
              name="brand"
              value={brand}
            />
          </Form.Field>
          }

          {campaignType !== 'ACR' &&
          <Form.Field>
            <label htmlFor="campaign-currencies">Currency</label>
            <Dropdown
              fluid
              search
              style={currencyDropDownStyle}
              onChange={this.onCurrencyChanged}
              value={currency || CURRENCY_GBP}
              name="currency"
              options={DYZIO_CURRENCIES}
            />
          </Form.Field>
          }

          {campaignType !== 'ACR' &&
          <Form.Field>
            <label htmlFor="campaign-budget">Total Budget<RequiredLabel /> <Popup
              content="The Total Budget is added to the Influencer Fees section,
                and is used to calculate the allocated and remaining budget on your campaign.
                Please add zero if it's a free / gifted campaign."
              trigger={<Icon name="help circle" className="circular" />}
            /></label>
            <Form.Input
              type="number"
              onChange={this.onInputChanged}
              placeholder="e.g. £10,000"
              name="budget"
              value={budget}
            />
          </Form.Field>
          }

          {campaignType !== 'ACR' &&
          <Form.Field>
            <label htmlFor="campaign-live-date">Live Date<RequiredLabel /></label>
            <DatePicker
              onChange={this.onLiveDateChange}
              placeholderText="e.g. 01/05/2018"
              selected={liveDateMoment ? liveDateMoment.set({ hour: 15, minute: 15 }) : null}
              dateFormat="MMM Do YYYY"
              selectsStart
              endDate={safeEndDate}
              maxDate={safeEndDate}
            />
          </Form.Field>
          }

          {
            campaignType !== 'ACR' &&
            <Form.Field>
              <label htmlFor="campaign-end-date">End Date<RequiredLabel /></label>
              <DatePicker
                onChange={this.onEndDateChange}
                placeholderText="e.g. 20/05/2018"
                selected={endDateMoment ? endDateMoment.set({ hour: 15, minute: 15 }) : null}
                endDate={safeEndDate}
                minDate={safeLiveDate}
                startDate={safeLiveDate}
                selectsEnd
                dateFormat="MMM Do YYYY"

              />
            </Form.Field>
          }

          {
            campaignType !== 'ACR' &&
            <Form.Field>
              <label htmlFor="campaign-country">Country (optional)</label>
              <Dropdown
                name="country"
                placeholder="e.g United Kingdom"
                search
                selection
                value={country}
                onChange={this.onInputChanged}
                options={countryOptions}
              />
            </Form.Field>
          }

          {
            campaignType !== 'ACR' &&
            <HashtagsInput
              onValueChanged={this.onInputChanged}
              campaignType={campaignType}
              hashtags={tags}
            />
          }

          {
            inAdvancedFormMode && (
              <div>
                <Form.Field>
                  <label htmlFor="campaign-code">Campaign Code</label>
                  <Form.Input
                    onChange={this.onInputChanged}
                    placeholderText="e.g. nike2020"
                    name="campaignCode"
                    value={campaignCode}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    name="sendChaseEmails"
                    onChange={this.onInputChanged}
                    label="Send chase email"
                    defaultChecked={sendChaseEmails}
                    checked={sendChaseEmails}
                  />
                </Form.Field>
              </div>
            )
          }

          { FEATURE_TOGGLE_STICKERS &&
            <Form.Field style={borderStyle}>
              { upgradePlan && <label htmlFor="story-scanner" style={marginBottom10}>
            These features are available on paid plans. Click <TargetLink href={MANAGE_ORG_LINK} renderIf>
              here</TargetLink> to upgrade.
              </label> }
              <div>
                <Radio
                  toggle checked={isStickerScannerEnabled}
                  disabled
                /> <span style={radioLabelStyle} >Capture Instagram Story Media</span>
              </div>
              <div>
                <Radio
                  toggle checked={useEstimatedReachAndImpressions}
                  disabled={!isEstimatedReachAndImpressionsEnabled}
                  onChange={() => {
                    this.setState(
                      { useEstimatedReachAndImpressions: !useEstimatedReachAndImpressions },
                    );
                  }
                  }
                /> <span style={radioLabelStyle} >Estimated Reach & Impression</span>
              </div>

            </Form.Field>
          }

        </Segment>
        {
          inAdvancedFormMode &&
          <Segment>
            <h3>Report Settings</h3>
            <div style={dividerStyle} />
            <Form.Field inline>
              <label htmlFor="cmp">Impressions CPM</label>
              <Divider hidden fitted />
              <Input
                onChange={this.onInputChanged}
                name="impressionsCPM"
                value={impressionsCPM}
                type="number"
                style={cmpStyle}
                labelPosition="left"
                fluid
                step="0.01"
                min="0"
                placeholder="Enter CPM..."
              >
                <Label basic>{renderCurrencySymbol(currency)}</Label>
                <input />
              </Input>
            </Form.Field>
            <p>
              Add an impressions CPM in order to create a top level campaign Earned Media Value (EMV) report.
              Find out more <TargetLink href={EMV_HELP_LINK} renderIf>here</TargetLink>.
            </p>
            <Divider />
            <Form.Field inline>
              <label htmlFor="cmp">Engagements CPM</label>
              <Divider hidden fitted />
              <Input
                onChange={this.onInputChanged}
                name="engagementsCPM"
                value={engagementsCPM}
                type="number"
                style={cmpStyle}
                labelPosition="left"
                fluid
                step="0.01"
                min="0"
                placeholder="Enter CPM..."
              >
                <Label basic>{renderCurrencySymbol(currency)}</Label>
                <input />
              </Input>
            </Form.Field>
            <p>
              Add an engagements CPM in order to create a top level campaign Earned Media Value (EMV) report.
              Find out more <TargetLink href={EMV_HELP_LINK} renderIf>here</TargetLink>.
            </p>
            <Divider />
            <Form.Field inline>
              <label htmlFor="cmp">I/E Ratio</label>
              <Divider hidden fitted />
              <Input
                onChange={this.onInputChanged}
                name="ieRatio"
                value={ieRatio}
                type="number"
                style={cmpStyle}
                labelPosition="left"
                fluid
                step="0.01"
                min="0"
                placeholder="Enter CPM..."
              >
                <Label basic>φ</Label>
                <input />
              </Input>
            </Form.Field>
            <p>
              The ratio of impressions to engagements for an influencer.
              This is used to estimate impressions for an influencer who has not authorised.
              A typical value would be around 10. Find out more <TargetLink href={EMV_HELP_LINK} renderIf>here</TargetLink>.
            </p>
          </Segment>
        }

        <Divider hidden />

        {
          shouldWarnPastLiveDate && (
            <Message negative>
              <MessageHeader><Icon name="warning circle" />Your campaign starts in the past</MessageHeader>
              <p>Dyzio cannot track stories posted earlier than 24 hours ago</p>
            </Message>
          )
        }


        <Grid stretched>
          <Grid.Row>
            {onBackClick &&
            <Grid.Column width={6}>
              <Button
                fluid
                type="button"
                onClick={onBackClick}
              >
              Back
              </Button>
            </Grid.Column>}
            <Grid.Column width={onBackClick ? 10 : 16}>
              <Form.Field>
                <DyzioPrimaryButton
                  fluid
                  disabled={isSubmitButtonDisabled}
                  label={inEditMode ? 'Save campaign' : 'Add campaign'}
                  hasIcon
                  icon={<Icon name="save" />}
                  floated={onBackClick ? 'right' : 'left'}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>

      </Form>
    );
  }
}

NewCampaignForm.propTypes = {
  formType: PropTypes.oneOf(_.values(FORM_TYPES)),
  inEditMode: PropTypes.bool,
  onFormSubmit: PropTypes.func.isRequired,
  campaignType: PropTypes.string.isRequired,
  onBackClick: PropTypes.func,
  organisation: PropTypes.object.isRequired,
};

NewCampaignForm.defaultProps = {
  onBackClick: null,
  inEditMode: false,
  formType: FORM_TYPES.BASIC,
};

export default compose(
  hasOrganisation,
)(NewCampaignForm);



/** WEBPACK FOOTER **
 ** ./src/components/new-campaign-form/index.js
 **/